import { APP_CONFIG } from '../../../../configs/config';
import { getNetworkFailureMessage, mapApiDataToFieldGroups } from '../utils';

const buildBusinessCardPayload = ({
  username,
  primaryFieldValues,
  additionalFields,
  imagePreview
}) => {
  const basePayload = {
    username,
    image_url: imagePreview || null,
    title: primaryFieldValues.title || null,
    full_name: primaryFieldValues.full_name || '',
    position: primaryFieldValues.position || null,
    email: primaryFieldValues.email || null,
    phone_number: primaryFieldValues.phone_number || null,
    organization: primaryFieldValues.organization || null,
    address: primaryFieldValues.address || null,
    others: null
  };

  if (Array.isArray(additionalFields) && additionalFields.length > 0) {
    const validEntries = additionalFields
      .filter((item) => item.attribute)
      .reduce((accumulator, item) => {
        accumulator[item.attribute] = item.value ?? '';
        return accumulator;
      }, {});
    if (Object.keys(validEntries).length > 0) {
      basePayload.others = validEntries;
    }
  }

  return basePayload;
};

const postUpdatedCard = async (payload, setError) => {
  try {
    const response = await fetch(`${APP_CONFIG.API_BASE_URL}/business-cards/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let message = `Update failed: ${response.status}`;
      try {
        const data = await response.json();
        message = data?.detail || message;
      } catch (error) {
        /* ignore json parsing errors */
      }
      throw new Error(message);
    }
  } catch (error) {
    const message = error?.message || 'Unable to update business card.';
    setError(message);
    throw error;
  }
};

const processUploadedData = ({ data, requestId, activeRequestIdRef, setFieldGroups, setPrimaryFieldValues, setAdditionalFields, setImagePreview, setScanError }) => {
  if (requestId !== activeRequestIdRef.current) {
    return;
  }

  const groups = mapApiDataToFieldGroups(data);
  setFieldGroups(groups);
  setPrimaryFieldValues(() => {
    const next = {};
    groups.primaryFields.forEach((field) => {
      next[field.key] = field.value;
    });
    return next;
  });
  setAdditionalFields(groups.additionalFields);
  if (data.image_url) {
    setImagePreview(data.image_url);
  }
  setScanError('');
};

const handleUploadError = ({ error, requestId, activeRequestIdRef, setScanError }) => {
  if (requestId !== activeRequestIdRef.current) {
    return;
  }
  let message = 'Failed to extract info. You can edit manually.';
  const errorMessage = error?.message || '';
  if (errorMessage) {
    if (errorMessage.includes('Failed to fetch')) {
      message = getNetworkFailureMessage();
    } else if (errorMessage.includes('502')) {
      message = 'Our recognition service is unavailable. Please try again later or enter details manually.';
    } else if (errorMessage.includes('500')) {
      message = 'Something went wrong on the server. Please retry or edit the fields yourself.';
    } else if (errorMessage.includes('username must not be blank')) {
      message = 'Username is required to save contacts. Please sign in or retry.';
    } else if (errorMessage.length < 160) {
      message = errorMessage;
    }
  }
  setScanError(message);
};

const fetchBusinessCard = async ({ formData, setScanError, setIsScanning, setShowRateLimitModal, setRateLimitMessage }) => {
  const response = await fetch(`${APP_CONFIG.API_BASE_URL}/business-cards`, {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    // Handle rate limit - show modal
    if (response.status === 429) {
      let message = 'You have reached the maximum number of requests. Please sign in to continue using this service.';
      try {
        const errorJson = await response.json();
        message = errorJson?.detail || message;
      } catch (parseErr) {
        /* ignore */
      }
      
      // Show rate limit modal
      if (setShowRateLimitModal && setRateLimitMessage) {
        setRateLimitMessage(message);
        setShowRateLimitModal(true);
      }
      
      throw new Error(message);
    }
    
    let message = `Request failed: ${response.status}`;
    const cloned = response.clone();
    try {
      const errorJson = await response.json();
      message = errorJson?.detail || message;
    } catch (parseErr) {
      try {
        const text = await cloned.text();
        if (text) {
          message = text;
        }
      } catch (textErr) {
        /* ignore */
      }
    }
    throw new Error(message);
  }

  return response.json();
};

export {
  buildBusinessCardPayload,
  postUpdatedCard,
  processUploadedData,
  handleUploadError,
  fetchBusinessCard
};
